import { ReactNode, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { motion, AnimatePresence } from 'framer-motion';

interface LayoutProps {
  children: ReactNode;
}

const navItems = [
  { path: ROUTE_PATHS.HOME, label: '首页', emoji: '🏠' },
  { path: ROUTE_PATHS.LEARN, label: '今日学习', emoji: '📚' },
  { path: ROUTE_PATHS.REVIEW, label: '复习', emoji: '🔄' },
  { path: ROUTE_PATHS.PHONICS, label: '发音', emoji: '🎵' },
  { path: ROUTE_PATHS.PROGRESS, label: '进度', emoji: '📊' },
  { path: ROUTE_PATHS.REWARDS, label: '奖励', emoji: '⚡' },
  { path: ROUTE_PATHS.PARENT, label: '家长', emoji: '👨‍👩‍👧' },
];

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden md:flex md:flex-col md:fixed md:left-0 md:top-0 md:h-screen md:w-60 bg-card border-r border-border z-40">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-3xl">🐼</span>
            <span className="text-3xl">🦊</span>
            <span className="text-3xl">🐰</span>
          </div>
          <h1 className="text-lg font-bold text-foreground font-heading leading-tight mt-2">
            宥宥和朋友的语言家
          </h1>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                    : 'text-foreground hover:bg-secondary'
                }`
              }
            >
              <span className="text-2xl">{item.emoji}</span>
              <span className="text-base font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex-1 md:ml-60">
        <header className="md:hidden sticky top-0 z-50 w-full border-b border-border bg-card shadow-sm">
          <div className="px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🐼</span>
              <span className="text-2xl">🦊</span>
              <span className="text-2xl">🐰</span>
            </div>
            <h1 className="text-sm font-bold text-foreground font-heading flex-1 text-center">
              宥宥和朋友的语言家
            </h1>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-secondary transition-colors"
              aria-label="菜单"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-foreground" />
              ) : (
                <Menu className="w-6 h-6 text-foreground" />
              )}
            </button>
          </div>
        </header>

        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="md:hidden fixed inset-0 bg-black/50 z-40"
                onClick={() => setMobileMenuOpen(false)}
              />
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="md:hidden fixed top-16 right-0 bottom-0 w-72 bg-card border-l border-border z-50 shadow-2xl"
              >
                <nav className="p-4 space-y-1 overflow-y-auto h-full">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                          isActive
                            ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                            : 'text-foreground hover:bg-secondary'
                        }`
                      }
                    >
                      <span className="text-2xl">{item.emoji}</span>
                      <span className="text-base font-medium">{item.label}</span>
                    </NavLink>
                  ))}
                </nav>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        <main className="min-h-screen">{children}</main>
      </div>
    </div>
  );
}
